import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import _ from "lodash";
import { sendAsync } from "../../../app/applicationSlice";
import { RootDispatch, RootState, RootThunkApi } from "../../../app/store";
import { fromMaterialJson, Material } from "../../../models/material";
import * as R from "../../../models/recipe";
import { Recipe } from "../../../models/recipe";
import * as SR from "../../../services/recipe";
import { CreateRetailRecipeParams } from "../../../services/recipe";
import { fixGroupNumber } from "../../../utils/MaterialUtils";

interface RecipeEditorState {
  recipe?: Recipe;
  materials: Material[];
}

const initialState = {
  materials: [],
} as RecipeEditorState;

function reloadRecipeMaterials({
  dispatch,
  getState,
}: {
  dispatch: RootDispatch;
  getState: () => RootState;
}) {
  const recipe = selectRecipe(getState());
  if (recipe) {
    dispatch(getRecipeDetail(recipe.id));
  }
}

export const createOrUpdateRecipe = createAsyncThunk<
  string,
  Recipe,
  RootThunkApi<string>
>("recipeEditor/createOrUpdateRecipe", async (recipe, api) => {
  return sendAsync(SR.createOrUpdateRecipe(recipe), api).then((data) => {
    reloadRecipeMaterials(api);
    return data?.toString() || "";
  });
});

export const createRetailRecipe = createAsyncThunk<
  Recipe,
  CreateRetailRecipeParams,
  RootThunkApi<Recipe>
>("recipeEditor/createRetailRecipeStatus", async (params, api) => {
  return sendAsync(
    SR.createRetailRecipe(params),
    api
  ).then((data: Record<string, never>) => R.fromJson(data));
});

export const copyRecipe = createAsyncThunk<
  void,
  SR.CopyRecipeParams,
  RootThunkApi<void>
>("recipeEditor/copyRecipeStatus", async (params, api) => {
  return sendAsync(SR.copyRecipe(params), api).then(() => {
    reloadRecipeMaterials(api);
  });
});

export const getRecipeDetail = createAsyncThunk<
  [Recipe, Material[]],
  string,
  RootThunkApi<[Recipe, Material[]]>
>("recipeEditor/getRecipeDetailStatus", async (param, api) => {
  return sendAsync(SR.getRecipeDetail(param), api).then(
    (data: { records: [Record<string, never>] }) => {
      const items =
        _.chain(data.records?.[0]?.itemList)
          .map((m) => fromMaterialJson(m))
          .sort((a, b) => {
            if (a.groupNumber < b.groupNumber) {
              return -1;
            }
            if (a.groupNumber > b.groupNumber) {
              return 1;
            }
            return a.sort - b.sort;
          })
          .value() || [];
      return [R.fromJson(data.records[0]), items] as [Recipe, Material[]];
    }
  );
});

export const saveRecipeMaterials = createAsyncThunk<
  void,
  SR.SaveRecipeMaterialsParams,
  RootThunkApi<void>
>("recipeEditor/saveRecipeMaterials", async (params, api) => {
  return sendAsync(SR.saveRecipeMaterials(params), api).then(() => {
    reloadRecipeMaterials(api);
  });
});

export const removeMaterials = createAsyncThunk<void, string[], RootThunkApi>(
  "recipeEditor/removeMaterialsStatus",
  async (params, api) => {
    await sendAsync(SR.removeMaterials(params), api);
    const materials = selectRecipeMaterials(api.getState()).filter(
      (m) => !params.includes(m.id)
    );
    api.dispatch(updateSortAndGroupNumber(fixGroupNumber(materials)));
  }
);

export const updateSortAndGroupNumber = createAsyncThunk<
  void,
  Material[],
  RootThunkApi<void>
>("recipeEditor/updateSortAndGroupNumberState", async (params, api) => {
  return sendAsync(SR.updateSortAndGroupNumber(params), api).then(() => {
    reloadRecipeMaterials(api);
  });
});

const recipeEditorSlice = createSlice({
  name: "recipeEditor",
  initialState,
  reducers: {
    resetRecipeEditor: (state) => {
      state.recipe = undefined;
      state.materials = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRecipeDetail.fulfilled, (state, action) => {
      state.recipe = action.payload[0];
      state.materials = action.payload[1];
    });
    builder.addCase(createRetailRecipe.fulfilled, (state, action) => {
      state.recipe = action.payload;
    });
  },
});

export const { resetRecipeEditor } = recipeEditorSlice.actions;

export const selectRecipe = (state: RootState): Recipe | undefined =>
  state.recipeEditor.recipe;

export const selectRecipeMaterials = (state: RootState): Material[] =>
  state.recipeEditor.materials;

export default recipeEditorSlice.reducer;
